import React, { memo, useMemo } from 'react';
import { View, Text, StyleSheet, Platform, Pressable, type TextStyle } from 'react-native';
import { SvgIcon } from '../../components/common/SvgIcon';
import { appTheme, type ThemeColors } from '../../shared/theme';
import { hexToRgba } from '../../shared/theme/colorUtils';
import { useRtl } from '../../shared/i18n/useRtl';
import { formatNumber } from '../../shared/i18n';
import {
  DASH_STUDIO_RADIUS,
  studioCardShell,
  androidScaleFont,
  androidScaleSize,
} from '../../shared/theme/studio/stackScreenShell';

const ANDROID_TEXT_COMPACT: TextStyle = Platform.OS === 'android' ? { includeFontPadding: false } : {};

type Props = {
  days: number;
  days_label: string;
  colors: ThemeColors;
  onPress: () => void;
};

export const SubscriptionCatalogDayTile = memo(function SubscriptionCatalogDayTile({ days, days_label, colors, onPress }: Props) {
  const rtl = useRtl();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const ripple_color = hexToRgba(colors.primary, 0.12) as string;

  return (
    <Pressable
      onPress={onPress}
      android_ripple={{ color: ripple_color }}
      style={({ pressed }) => [styles.tile, pressed && Platform.OS === 'ios' ? { opacity: 0.94 } : null]}
      accessibilityRole="button"
      accessibilityLabel={`${formatNumber(days)} ${days_label}`}>
      <Text style={styles.number}>{formatNumber(days)}</Text>
      <Text style={styles.unit}>{days_label}</Text>
      <View style={styles.footer}>
        <SvgIcon name={rtl.chevronNextAlongAxis} size={14} color={colors.primary} />
      </View>
    </Pressable>
  );
});

function createStyles(c: ThemeColors) {
  const sf = androidScaleFont;
  const ss = androidScaleSize;

  return StyleSheet.create({
    tile: {
      width: '48%',
      minHeight: ss(96),
      ...studioCardShell(c),
      borderRadius: DASH_STUDIO_RADIUS,
      paddingVertical: ss(12),
      paddingHorizontal: ss(12),
      alignItems: 'center',
      justifyContent: 'center',
      shadowColor: c.shadowColor,
      overflow: 'visible',
    },
    number: {
      fontSize: sf(28),
      lineHeight: sf(32),
      fontWeight: '800',
      letterSpacing: -0.5,
      color: c.primary,
      ...ANDROID_TEXT_COMPACT,
    },
    unit: {
      fontSize: sf(12),
      fontWeight: '700',
      color: c.textSecondary,
      textTransform: 'uppercase',
      letterSpacing: 0.5,
      marginTop: ss(2),
      ...ANDROID_TEXT_COMPACT,
    },
    footer: {
      flexDirection: 'row',
      alignItems: 'center',
      marginTop: ss(6),
      paddingHorizontal: ss(8),
      paddingVertical: ss(3),
      borderRadius: appTheme.borderRadius.pill,
      backgroundColor: hexToRgba(c.primary, 0.1),
    },
  });
}
